import React, { useRef, useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  CloseOutlined,
  StepBackwardOutlined,
  StepForwardOutlined,
} from '@ant-design/icons';
import styles from './VideoPlayer.module.scss';
import VideoElement from './VideoElement';
import VideoControls from './VideoControls';
import SeekBar from './SeekBar';
import RangeSlider from './RangeSlider';
import DrawingCanvas, { Shape, DrawingTool } from './DrawingCanvas';

export type { DrawingTool, Shape } from './DrawingCanvas';

const IMAGE_ZOOM_MIN = 0.5;
const IMAGE_ZOOM_MAX = 4;
const IMAGE_ZOOM_STEP = 0.25;
const SEEK_SKIP_FEEDBACK_MS = 750;

function clampImagePan(
  pan: { x: number; y: number },
  dispW: number,
  dispH: number,
  vw: number,
  vh: number,
) {
  const maxX = Math.max(0, (dispW - vw) / 2);
  const maxY = Math.max(0, (dispH - vh) / 2);
  return {
    x: Math.min(maxX, Math.max(-maxX, pan.x)),
    y: Math.min(maxY, Math.max(-maxY, pan.y)),
  };
}

/**
 * Compare: iki videoyu aynı kare indeksine hizalar (sürekli saniye seek kaymasını azaltır).
 * İkinci klip daha kısaysa aynı frame indeksi mümkün olduğunca korunur.
 */
function dualCompareSeekTimes(
  timeSec: number,
  durationMain: number,
  durationCompare: number | undefined,
  fps: number,
): { tMain: number; tCompare: number } {
  const f = fps > 0 ? fps : 30;
  const d1 = durationMain > 0 ? durationMain : 0;
  if (d1 <= 0) {
    const t = Math.max(0, timeSec);
    return { tMain: t, tCompare: t };
  }
  const clamped = Math.max(0, Math.min(d1, timeSec));
  const frame = Math.round(clamped * f);
  const lastF1 = Math.max(0, Math.floor(d1 * f) - 1);
  const fc = Math.max(0, Math.min(lastF1, frame));
  const tMain = fc / f;
  let tCompare = tMain;
  if (durationCompare !== undefined && Number.isFinite(durationCompare) && durationCompare > 0) {
    const lastF2 = Math.max(0, Math.floor(durationCompare * f) - 1);
    const fc2 = Math.min(fc, lastF2);
    tCompare = fc2 / f;
  }
  return { tMain, tCompare };
}

const RangePopupPauseGlyph = ({ className }: { className?: string }) => (
  <svg
    width={11}
    height={11}
    viewBox="0 0 20 20"
    aria-hidden
    className={className}
  >
    <rect x="5" y="4" width="4" height="12" rx="1" fill="currentColor" />
    <rect x="11" y="4" width="4" height="12" rx="1" fill="currentColor" />
  </svg>
);

interface VideoPlayerProps {
  src: string;
  fileType?: 'video' | 'image';
  isDrawingEnabled: boolean;
  title?: string;
  poster?: string;
  
  // Drawing props
  shapes: Shape[];
  tool: DrawingTool;
  color: string;
  onShapesChange: (shapes: Shape[]) => void;
  onTextToolClick: (pos: { x: number, y: number }) => void;
  selectedShapeId?: string | null;
  onShapeSelection?: (shapeId: string | null) => void;
  onDrawingComplete?: () => void;
  onCurrentTimeChange?: (time: number, fps: number) => void;
  currentVideoTime?: number;
  currentFrameIndex?: number;
  isPaused?: boolean;
  onPlay?: () => void;
  onSeek?: () => void;
  onDurationChange?: (duration: number) => void;
  selectedRange?: [number, number] | null;
  onRangeSelect?: (range: [number, number]) => void;
  /** Parent clears range state when user presses ESC while the range bar is visible */
  onRangeSelectCancel?: () => void;
  /** Aralık üstü play: çizim açıksa önce kapat (ör. setDrawingTool('none')) */
  onExitDrawingToolbox?: () => void;
  externalRange?: [number, number] | null;
  
  // Comments props
  comments?: Array<{
    id: number;
    startFrame: number;
    endFrame: number;
    text: string;
    shapes?: Shape[];
    user?: {
      firstName: string;
      lastName: string;
      email?: string;
      role?: string;
      thumbnailUrl?: string;
    };
    isResolved?: boolean;
  }>;
  onCommentClick?: (commentId: number) => void;
  selectedCommentId?: number | null;
  fps?: number;
  
  // Compare mode prop
  compareMode?: boolean;

  // Show resolved pins
  showResolved?: boolean;

  /** Alt kontrol çubuğunda (expand solunda) çizim araçları */
  controlBarToolbox?: React.ReactNode;

  /** Dual-display compare: second video source + drawing config */
  compareVideo?: {
    src: string;
    poster?: string;
    shapes: Shape[];
    onShapesChange: (shapes: Shape[]) => void;
    onTextToolClick: (pos: { x: number; y: number }) => void;
    selectedShapeId: string | null;
    onShapeSelection: (shapeId: string | null) => void;
    onDrawingComplete: () => void;
    isDrawingEnabled: boolean;
    onCurrentTimeChange?: (time: number, fps: number) => void;
    onPlay?: () => void;
    onSeek?: () => void;
    onDurationChange?: (duration: number) => void;
    onRangeSelect?: (range: [number, number]) => void;
    selectedRange?: [number, number] | null;
    externalRange?: [number, number] | null;
    controlBarToolbox?: React.ReactNode;
    comments?: Array<{
      id: number;
      startFrame: number;
      endFrame: number;
      text: string;
      shapes?: Shape[];
      user?: {
        firstName: string;
        lastName: string;
        email?: string;
        role?: string;
        thumbnailUrl?: string;
      };
      isResolved?: boolean;
    }>;
  };
  activeVideoPanel?: 1 | 2;
  onActiveVideoPanelChange?: (panel: 1 | 2) => void;
  /** Headers rendered above each video panel in dual mode */
  dualHeaders?: {
    main: React.ReactNode;
    compare: React.ReactNode;
  };

  /** Compare panel header X: 1 = sol, 2 = sağ */
  onComparePanelClose?: (panel: 1 | 2) => void;

  /** Compare + landscape yorum çekmecesi: yalnızca aktif videoyu göster (compare state açık kalır) */
  dualCompareShowSinglePanel?: boolean;
  /** Alt kontrol çubuğunda yorum çekmecesi tetikleyici */
  compareCommentToggleSlot?: React.ReactNode;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  fileType = 'video',
  isDrawingEnabled,
  title,
  poster,
  shapes,
  tool,
  color,
  onShapesChange,
  onTextToolClick,
  selectedShapeId,
  onShapeSelection,
  onDrawingComplete,
  onCurrentTimeChange,
  currentVideoTime,
  currentFrameIndex,
  isPaused,
  onPlay,
  onSeek,
  onDurationChange,
  selectedRange,
  onRangeSelect,
  onRangeSelectCancel,
  onExitDrawingToolbox,
  externalRange,
  comments = [],
  onCommentClick,
  selectedCommentId,
  fps = 30,
  compareMode = false,
  showResolved,
  controlBarToolbox,
  compareVideo,
  activeVideoPanel = 1,
  onActiveVideoPanelChange,
  dualHeaders,
  onComparePanelClose,
  dualCompareShowSinglePanel = false,
  compareCommentToggleSlot,
}) => {
  const isDualMode = !!compareVideo;
  const drawingToolActive = isDrawingEnabled && tool !== 'none';
  const compareDrawingToolActive = (compareVideo?.isDrawingEnabled ?? false) && tool !== 'none';
  const anyDrawingToolActive = drawingToolActive || compareDrawingToolActive;

  const videoRef = useRef<HTMLVideoElement>(null);
  const compareVideoRef = useRef<HTMLVideoElement>(null);
  /** Mute (M) restores this level when unmuting */
  const volumeBeforeMuteRef = useRef(1);
  const imgRef = useRef<HTMLImageElement>(null);
  const playerWrapperRef = useRef<HTMLDivElement>(null);
  const imageViewportRef = useRef<HTMLDivElement>(null);
  const imagePanDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origin: { x: number; y: number };
  } | null>(null);
  const prevImageSrcRef = useRef<string | null>(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageNaturalSize, setImageNaturalSize] = useState<{
    w: number;
    h: number;
  } | null>(null);
  const [imageViewportSize, setImageViewportSize] = useState({ w: 0, h: 0 });
  const [imagePan, setImagePan] = useState({ x: 0, y: 0 });
  const imagePanRef = useRef(imagePan);
  useEffect(() => {
    imagePanRef.current = imagePan;
  }, [imagePan]);
  const [imagePanDragging, setImagePanDragging] = useState(false);

  /** contain boyutu (100% zoom); zoom sadece .imagePanInner üzerinde CSS scale — img/Konva pikseli sabit */
  const imageTransform = useMemo(() => {
    if (fileType !== 'image' || !imageNaturalSize) return null;
    const vw = imageViewportSize.w;
    const vh = imageViewportSize.h;
    const nw = imageNaturalSize.w;
    const nh = imageNaturalSize.h;
    if (vw < 2 || vh < 2 || nw < 1 || nh < 1) return null;
    const fit = Math.min(vw / nw, vh / nh);
    const baseW = Math.max(1, Math.round(nw * fit));
    const baseH = Math.max(1, Math.round(nh * fit));
    const z = imageZoom;
    let visualScale = z;
    if (z > 1 && baseW * z < vw - 0.5) {
      visualScale = (vw * z) / baseW;
    }
    return {
      baseW,
      baseH,
      visualScale,
      dispW: baseW * visualScale,
      dispH: baseH * visualScale,
    };
  }, [fileType, imageNaturalSize, imageViewportSize, imageZoom]);

  const imageStageSize = useMemo(() => {
    if (!imageTransform) return { width: 0, height: 0 };
    return { width: imageTransform.baseW, height: imageTransform.baseH };
  }, [imageTransform]);

  const imagePanInteractionEnabled =
    fileType === 'image' && !drawingToolActive;

  const handleImagePanPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!imagePanInteractionEnabled) return;
      if (e.button !== 0) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      imagePanDragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        origin: { ...imagePanRef.current },
      };
      setImagePanDragging(true);
    },
    [imagePanInteractionEnabled],
  );

  const handleImagePanPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = imagePanDragRef.current;
      if (!d || d.pointerId !== e.pointerId || !imageTransform) return;
      e.preventDefault();
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      const { w: vw, h: vh } = imageViewportSize;
      setImagePan(
        clampImagePan(
          { x: d.origin.x + dx, y: d.origin.y + dy },
          imageTransform.dispW,
          imageTransform.dispH,
          vw,
          vh,
        ),
      );
    },
    [imageTransform, imageViewportSize],
  );

  const handleImagePanPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = imagePanDragRef.current;
      if (d && d.pointerId === e.pointerId) {
        imagePanDragRef.current = null;
        try {
          e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        setImagePanDragging(false);
      }
    },
    [],
  );

  const handleImageWheelZoom = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    if (fileType !== 'image') return;
    e.preventDefault();
    const direction = e.deltaY < 0 ? 1 : -1;
    setImageZoom((z) =>
      Math.max(IMAGE_ZOOM_MIN, Math.min(IMAGE_ZOOM_MAX, z + direction * IMAGE_ZOOM_STEP)),
    );
  }, [fileType]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);

  /** Compare: yalnızca seçili panelde ses; diğeri susturulur */
  const applyDualModeVolume = useCallback(
    (uiVolume: number) => {
      if (!isDualMode) {
        if (videoRef.current) videoRef.current.volume = uiVolume;
        return;
      }
      if (videoRef.current) {
        videoRef.current.volume = activeVideoPanel === 1 ? uiVolume : 0;
      }
      if (compareVideoRef.current) {
        compareVideoRef.current.volume = activeVideoPanel === 2 ? uiVolume : 0;
      }
    },
    [isDualMode, activeVideoPanel],
  );

  const [controlsVisible, setControlsVisible] = useState(false);
  const [seekSkipFeedback, setSeekSkipFeedback] = useState<{
    direction: 'back' | 'forward';
    id: number;
  } | null>(null);
  const seekSkipFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seekSkipFeedbackIdRef = useRef(0);
  const [isLooping, setIsLooping] = useState(false);
  const [isRangeLooping, setIsRangeLooping] = useState(false);
  const rangeABRef = useRef<[number, number]>([0, 0]);
  const isRangeLoopingRef = useRef(false);

  const effectiveFps = fps > 0 ? fps : 30;
  const minRangeSpan = Math.max(0.05, 1 / effectiveFps);

  /** Tek veya çift video: compare’da kare hizalı seek */
  const seekBothVideos = useCallback(
    (timeSec: number): number => {
      if (!videoRef.current) return 0;
      const d1 = duration || 0;
      if (!isDualMode) {
        const t = d1 > 0 ? Math.max(0, Math.min(d1, timeSec)) : Math.max(0, timeSec);
        videoRef.current.currentTime = t;
        setCurrentTime(t);
        return t;
      }
      const d2Raw = compareVideoRef.current?.duration;
      const d2 =
        Number.isFinite(d2Raw) && (d2Raw as number) > 0 ? (d2Raw as number) : undefined;
      const { tMain, tCompare } = dualCompareSeekTimes(timeSec, d1, d2, effectiveFps);
      videoRef.current.currentTime = tMain;
      if (compareVideoRef.current) compareVideoRef.current.currentTime = tCompare;
      setCurrentTime(tMain);
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console -- compare senkron teşhisi
        console.debug('[CompareSync] seekBothVideos', {
          input: timeSec,
          tMain,
          tCompare,
          d1,
          d2,
          fps: effectiveFps,
          driftMain: timeSec - tMain,
          driftCompareHint: timeSec - tCompare,
        });
      }
      return tMain;
    },
    [isDualMode, duration, effectiveFps],
  );

  const flashSeekSkipFeedback = useCallback((direction: 'back' | 'forward') => {
    if (seekSkipFeedbackTimerRef.current) {
      clearTimeout(seekSkipFeedbackTimerRef.current);
    }
    seekSkipFeedbackIdRef.current += 1;
    setSeekSkipFeedback({ direction, id: seekSkipFeedbackIdRef.current });
    seekSkipFeedbackTimerRef.current = setTimeout(() => {
      setSeekSkipFeedback(null);
      seekSkipFeedbackTimerRef.current = null;
    }, SEEK_SKIP_FEEDBACK_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (seekSkipFeedbackTimerRef.current) {
        clearTimeout(seekSkipFeedbackTimerRef.current);
      }
    };
  }, []);

  // Custom seekbar için
  const seekbarRef = useRef<HTMLDivElement>(null);

  // Range seçme modunda mini slider
  const [showRangeSlider, setShowRangeSlider] = useState(false);
  const [rangeSliderValue, setRangeSliderValue] = useState<[number, number]>([0, 0]);
  
  // selectedRange veya externalRange prop'u değiştiğinde rangeSliderValue'yu güncelle ve range bar'ı göster
  useEffect(() => {
    // externalRange öncelikli
    const activeRange = externalRange || selectedRange;
    
    if (activeRange) {
      if (activeRange[0] === 0 && activeRange[1] === 0) {
        // [0, 0] ise range bar'ı gizle
        setShowRangeSlider(false);
        setRangeSliderValue([0, 0]);
      } else {
        // Geçerli bir range ise göster
        setRangeSliderValue(activeRange);
        setShowRangeSlider(true);
      }
    } else {
      // Hiç range yoksa range bar'ı gizle
      setShowRangeSlider(false);
      setRangeSliderValue([0, 0]);
    }
  }, [selectedRange, externalRange]);

  /* Yorum seçili + externalRange: seek üstündeki range çizgisi yeterli; alttaki range slider gösterme */
  const hideRangeSliderForSelectedComment =
    selectedCommentId != null && externalRange != null;
  const rangeSliderVisible = showRangeSlider && !hideRangeSliderForSelectedComment;

  // Duration değiştiğinde range bar'ı sıfırla (sadece selectedRange ve externalRange yoksa)
  useEffect(() => {
    if (duration > 0 && !selectedRange && !externalRange) {
      setShowRangeSlider(false);
      setRangeSliderValue([0, 0]);
    }
  }, [duration, selectedRange, externalRange]);

  useEffect(() => {
    const d = duration || 0;
    const raw0 = rangeSliderValue[0];
    const raw1 = rangeSliderValue[1];
    const a = Math.max(0, Math.min(raw0, d));
    const b = Math.max(a, Math.min(raw1, d));
    rangeABRef.current = [a, b];
  }, [rangeSliderValue, duration]);

  const rangeSpan = rangeSliderValue[1] - rangeSliderValue[0];
  const hasSegmentRange =
    duration > 0 &&
    !(rangeSliderValue[0] === 0 && rangeSliderValue[1] === 0) &&
    rangeSpan >= minRangeSpan;

  const dForRangeUi = duration || 0;
  const rangeUi0 = Math.max(0, Math.min(rangeSliderValue[0], dForRangeUi));
  const rangeUi1 = Math.max(rangeUi0, Math.min(rangeSliderValue[1], dForRangeUi));
  /** 0–1: zaman ekseninde aralığın ortası (Ant Slider rayı yatay inset ile hizalanacak) */
  const rangePlayCenterFraction =
    dForRangeUi > 0 ? (rangeUi0 + rangeUi1) / 2 / dForRangeUi : 0.5;

  useEffect(() => {
    isRangeLoopingRef.current = isRangeLooping;
  }, [isRangeLooping]);

  useEffect(() => {
    if (!hasSegmentRange) {
      setIsRangeLooping(false);
    }
  }, [hasSegmentRange]);

  // Tam video döngüsü; aralık döngüsü açıkken native loop kapalı.
  // Compare modunda yalnızca ana videoya loop vermek ikinci videoyu bozar; döngü handleTimeUpdate / ended ile ortak.
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.loop = !!(isLooping && !isRangeLooping && !isDualMode);
    }
    if (compareVideoRef.current) {
      compareVideoRef.current.loop = false;
    }
  }, [isLooping, isRangeLooping, isDualMode]);

  /** Ana oynat/duraklat: seek çubuğundaki aralık döngüsünden çıkar (loopta ayrı buton var). */
  const exitRangeLoopFromMainTransport = () => {
    setIsRangeLooping(false);
    isRangeLoopingRef.current = false;
  };

  const handleCancelRangeSelection = useCallback(() => {
    setIsRangeLooping(false);
    isRangeLoopingRef.current = false;
    setShowRangeSlider(false);
    setRangeSliderValue([0, 0]);
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
      compareVideoRef.current?.pause();
      setIsPlaying(false);
    }
    onRangeSelectCancel?.();
  }, [onRangeSelectCancel]);

  const showRangeLoopCancel =
    rangeSliderVisible || isRangeLooping;

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        compareVideoRef.current?.pause();
        exitRangeLoopFromMainTransport();
        const currentTime = videoRef.current.currentTime;
        onRangeSelect?.([currentTime, currentTime]);
        compareVideo?.onRangeSelect?.([currentTime, currentTime]);
        setRangeSliderValue([currentTime, currentTime]);
        setShowRangeSlider(true);
      } else {
        if (anyDrawingToolActive) {
          return;
        }
        exitRangeLoopFromMainTransport();
        videoRef.current.play();
        compareVideoRef.current?.play();
        onPlay?.();
        compareVideo?.onPlay?.();
        setShowRangeSlider(false);
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    let t = v.currentTime;
    const [rs, re] = rangeABRef.current;
    const epsilon = Math.max(0.05, 1 / effectiveFps);

    if (isRangeLoopingRef.current) {
      if (t >= re - epsilon) {
        let tMain = rs;
        let tComp = rs;
        if (isDualMode && compareVideoRef.current && duration > 0) {
          const d2 = compareVideoRef.current.duration;
          const pair = dualCompareSeekTimes(
            rs,
            duration,
            Number.isFinite(d2) && d2 > 0 ? d2 : undefined,
            effectiveFps,
          );
          tMain = pair.tMain;
          tComp = pair.tCompare;
        }
        v.currentTime = tMain;
        if (compareVideoRef.current) compareVideoRef.current.currentTime = tComp;
        t = tMain;
      }
    } else if (
      isDualMode &&
      isLooping &&
      !isRangeLoopingRef.current &&
      duration > 0 &&
      compareVideoRef.current
    ) {
      const d2Raw = compareVideoRef.current.duration;
      const d2v =
        Number.isFinite(d2Raw) && (d2Raw as number) > 0 ? (d2Raw as number) : duration;
      const dClip = Math.min(duration, d2v);
      if (t >= dClip - epsilon) {
        const pair = dualCompareSeekTimes(0, duration, d2v, effectiveFps);
        v.currentTime = pair.tMain;
        compareVideoRef.current.currentTime = pair.tCompare;
        t = pair.tMain;
      }
    }

    setCurrentTime(t);
    onCurrentTimeChange?.(t, fps);
    compareVideo?.onCurrentTimeChange?.(t, fps);
  };

  const handleToggleFullLoop = useCallback(() => {
    setIsRangeLooping(false);
    setIsLooping((v) => !v);
  }, []);

  const handleRangeLoopPlayClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (!videoRef.current || !hasSegmentRange) return;

      if (anyDrawingToolActive) {
        onExitDrawingToolbox?.();
      }

      if (isPlaying && isRangeLooping) {
        videoRef.current.pause();
        compareVideoRef.current?.pause();
        setIsPlaying(false);
        return;
      }

      if (!isPlaying && isRangeLooping) {
        void videoRef.current.play();
        void compareVideoRef.current?.play();
        setIsPlaying(true);
        onPlay?.();
        compareVideo?.onPlay?.();
        return;
      }

      const [rs] = rangeABRef.current;
      setIsLooping(false);
      setIsRangeLooping(true);
      let tMain = rs;
      let tComp = rs;
      if (isDualMode && compareVideoRef.current && duration > 0) {
        const d2 = compareVideoRef.current.duration;
        const pair = dualCompareSeekTimes(
          rs,
          duration,
          Number.isFinite(d2) && d2 > 0 ? d2 : undefined,
          effectiveFps,
        );
        tMain = pair.tMain;
        tComp = pair.tCompare;
      }
      videoRef.current.currentTime = tMain;
      if (compareVideoRef.current) compareVideoRef.current.currentTime = tComp;
      setCurrentTime(tMain);
      void videoRef.current.play();
      void compareVideoRef.current?.play();
      setIsPlaying(true);
      onPlay?.();
      compareVideo?.onPlay?.();
      onSeek?.();
      compareVideo?.onSeek?.();
    },
    [
      hasSegmentRange,
      anyDrawingToolActive,
      onExitDrawingToolbox,
      isPlaying,
      isRangeLooping,
      onPlay,
      onSeek,
      compareVideo,
      isDualMode,
      duration,
      effectiveFps,
    ],
  );

  const handleVideoEnded = useCallback(() => {
    if (videoRef.current && isRangeLoopingRef.current) {
      const [rs] = rangeABRef.current;
      let tMain = rs;
      let tComp = rs;
      if (isDualMode && compareVideoRef.current && duration > 0) {
        const d2 = compareVideoRef.current.duration;
        const pair = dualCompareSeekTimes(
          rs,
          duration,
          Number.isFinite(d2) && d2 > 0 ? d2 : undefined,
          effectiveFps,
        );
        tMain = pair.tMain;
        tComp = pair.tCompare;
      }
      videoRef.current.currentTime = tMain;
      if (compareVideoRef.current) compareVideoRef.current.currentTime = tComp;
      void videoRef.current.play();
      void compareVideoRef.current?.play();
      return;
    }
    if (
      isDualMode &&
      isLooping &&
      !isRangeLoopingRef.current &&
      videoRef.current &&
      compareVideoRef.current &&
      duration > 0
    ) {
      const d2 = compareVideoRef.current.duration;
      const pair = dualCompareSeekTimes(
        0,
        duration,
        Number.isFinite(d2) && d2 > 0 ? d2 : undefined,
        effectiveFps,
      );
      videoRef.current.currentTime = pair.tMain;
      compareVideoRef.current.currentTime = pair.tCompare;
      void videoRef.current.play();
      void compareVideoRef.current.play();
      return;
    }
    compareVideoRef.current?.pause();
    setIsPlaying(false);
  }, [isDualMode, duration, effectiveFps, isLooping]);

  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [compareStageSize, setCompareStageSize] = useState({ width: 0, height: 0 });

  const getContainedSize = (video: HTMLVideoElement) => {
    const containerW = video.clientWidth;
    const containerH = video.clientHeight;
    const intrinsicW = video.videoWidth;
    const intrinsicH = video.videoHeight;
    if (!intrinsicW || !intrinsicH) return { width: containerW, height: containerH };
    const scale = Math.min(containerW / intrinsicW, containerH / intrinsicH);
    return { width: Math.round(intrinsicW * scale), height: Math.round(intrinsicH * scale) };
  };

  const updateStageSize = () => {
    if (videoRef.current) {
      setStageSize(getContainedSize(videoRef.current));
    }
  };

  const updateCompareStageSize = useCallback(() => {
    if (compareVideoRef.current) {
      setCompareStageSize(getContainedSize(compareVideoRef.current));
    }
  }, []);

  // Video yüklendiğinde ve pencere yeniden boyutlandığında güncelle
  useEffect(() => {
    updateStageSize();
    window.addEventListener('resize', updateStageSize);
    return () => window.removeEventListener('resize', updateStageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only init
  }, []);

  // Video boyutu değiştiğinde canvas boyutunu güncelle
  useEffect(() => {
    if (fileType !== 'video') return;
    const resizeObserver = new ResizeObserver(() => {
      updateStageSize();
    });

    if (videoRef.current) {
      resizeObserver.observe(videoRef.current);
    }

    if (videoRef.current?.parentElement) {
      resizeObserver.observe(videoRef.current.parentElement);
    }

    return () => {
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateStageSize stable ref
  }, [fileType]);

  useEffect(() => {
    if (!isDualMode || fileType !== 'video') return;
    const ro = new ResizeObserver(() => updateCompareStageSize());
    if (compareVideoRef.current) {
      ro.observe(compareVideoRef.current);
    }
    if (compareVideoRef.current?.parentElement) {
      ro.observe(compareVideoRef.current.parentElement);
    }
    return () => ro.disconnect();
  }, [isDualMode, fileType, updateCompareStageSize]);

  useEffect(() => {
    if (compareMode || isDualMode) {
      const updateAll = () => {
        updateStageSize();
        if (isDualMode) updateCompareStageSize();
      };
      const timer1 = setTimeout(updateAll, 100);
      const timer2 = setTimeout(updateAll, 300);
      const timer3 = setTimeout(updateAll, 500);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareMode, isDualMode]);

  useEffect(() => {
    if (!isDualMode || fileType !== 'video') return;
    const id = requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
    return () => cancelAnimationFrame(id);
  }, [dualCompareShowSinglePanel, isDualMode, fileType]);

  useEffect(() => {
    if (fileType !== 'image') return;
    if (prevImageSrcRef.current === src) return;
    prevImageSrcRef.current = src;
    setImageNaturalSize(null);
    setImagePan({ x: 0, y: 0 });
  }, [src, fileType]);

  useEffect(() => {
    if (fileType !== 'image') return;
    const el = imageViewportRef.current;
    if (!el) return;
    const read = () => {
      setImageViewportSize({ w: el.clientWidth, h: el.clientHeight });
    };
    const ro = new ResizeObserver(() => read());
    ro.observe(el);
    read();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(read);
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      ro.disconnect();
    };
  }, [fileType]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca layout/zoom’da pan sınırı
  useEffect(() => {
    if (fileType !== 'image' || !imageTransform) return;
    const { dispW, dispH } = imageTransform;
    const { w: vw, h: vh } = imageViewportSize;
    if (vw < 2 || vh < 2) return;
    setImagePan((p) => clampImagePan(p, dispW, dispH, vw, vh));
  }, [fileType, imageTransform, imageViewportSize, imageZoom]);

  // Video metadata yüklendiğinde de güncelle
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      onDurationChange?.(videoRef.current.duration);
      updateStageSize();
    } else if (fileType === 'image' && imgRef.current) {
      setImageNaturalSize({
        w: imgRef.current.naturalWidth,
        h: imgRef.current.naturalHeight,
      });
    }
  };
  
  const handleCompareLoadedMetadata = () => {
    if (compareVideoRef.current) {
      compareVideo?.onDurationChange?.(compareVideoRef.current.duration);
      updateCompareStageSize();
    }
  };

  const handleVolumeChange = (value: number) => {
    if (value > 0.001) {
      volumeBeforeMuteRef.current = value;
    }
    setVolume(value);
  };

  useLayoutEffect(() => {
    applyDualModeVolume(volume);
  }, [applyDualModeVolume, volume]);

  const handleToggleMute = useCallback(() => {
    setVolume((prev) => {
      if (prev > 0.001) {
        volumeBeforeMuteRef.current = prev;
        return 0;
      }
      const restore = volumeBeforeMuteRef.current > 0.001 ? volumeBeforeMuteRef.current : 1;
      return restore;
    });
  }, []);
  
  const handleFullScreen = useCallback(() => {
    const el = playerWrapperRef.current;
    if (!el) return;
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
      webkitExitFullscreen?: () => Promise<void>;
    };
    const fsEl =
      document.fullscreenElement ??
      doc.webkitFullscreenElement ??
      null;
    if (fsEl === el) {
      if (document.exitFullscreen) {
        void document.exitFullscreen();
      } else if (doc.webkitExitFullscreen) {
        void doc.webkitExitFullscreen();
      }
      return;
    }
    if (el.requestFullscreen) {
      void el.requestFullscreen();
    } else if ((el as unknown as HTMLElement & { webkitRequestFullscreen?: () => void }).webkitRequestFullscreen) {
      void (el as unknown as HTMLElement & { webkitRequestFullscreen: () => void }).webkitRequestFullscreen();
    }
  }, []);

  React.useEffect(() => {
    if (currentVideoTime === undefined || !videoRef.current) return;
    const d1 = duration || 0;
    let target = currentVideoTime;
    if (isDualMode && d1 > 0 && compareVideoRef.current) {
      const d2 = compareVideoRef.current.duration;
      target = dualCompareSeekTimes(
        currentVideoTime,
        d1,
        Number.isFinite(d2) && d2 > 0 ? d2 : undefined,
        effectiveFps,
      ).tMain;
    }
    const timeDifference = Math.abs(videoRef.current.currentTime - target);
    if (timeDifference > 0.1) {
      videoRef.current.currentTime = target;
      if (compareVideoRef.current) {
        if (isDualMode && d1 > 0) {
          const d2 = compareVideoRef.current.duration;
          compareVideoRef.current.currentTime = dualCompareSeekTimes(
            target,
            d1,
            Number.isFinite(d2) && d2 > 0 ? d2 : undefined,
            effectiveFps,
          ).tCompare;
        } else {
          compareVideoRef.current.currentTime = target;
        }
      }
      setCurrentTime(target);
    }
  }, [currentVideoTime, isDualMode, duration, effectiveFps]);

  const handleSeekbarClick = (e: React.MouseEvent<HTMLDivElement> | { clientX: number }) => {
    if (!duration || !videoRef.current || !seekbarRef.current) return;
    const rect = seekbarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const rawTime = percent * duration;
    const time = seekBothVideos(rawTime);
    onSeek?.();
    compareVideo?.onSeek?.();

    onRangeSelect?.([time, time]);
    compareVideo?.onRangeSelect?.([time, time]);

    setShowRangeSlider(true);
    setRangeSliderValue([time, time]);
  };

  const handleSeekbarDrag = (e: React.MouseEvent<HTMLDivElement> | { clientX: number }) => {
    if (!duration || !videoRef.current || !seekbarRef.current) return;
    const rect = seekbarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const rawTime = percent * duration;
    const time = seekBothVideos(rawTime);
    onSeek?.();
    compareVideo?.onSeek?.();

    onRangeSelect?.([time, time]);
    compareVideo?.onRangeSelect?.([time, time]);
  };



  const handleVideoClick = (e: React.MouseEvent) => {
    if (anyDrawingToolActive) return;
    if (!videoRef.current || duration === 0) return;
    if (showRangeSlider) setShowRangeSlider(false);
    handlePlayPause();
  };

  useEffect(() => {
    if (typeof currentFrameIndex !== 'number' || !videoRef.current) return;
    const eff = fps > 10 ? fps : 30;
    const seekTime = currentFrameIndex / eff;
    if (Math.abs(videoRef.current.currentTime - seekTime) <= 0.01) return;
    const d1 = duration || 0;
    if (isDualMode && compareVideoRef.current && d1 > 0) {
      const d2 = compareVideoRef.current.duration;
      const { tMain, tCompare } = dualCompareSeekTimes(
        seekTime,
        d1,
        Number.isFinite(d2) && d2 > 0 ? d2 : undefined,
        eff,
      );
      videoRef.current.currentTime = tMain;
      compareVideoRef.current.currentTime = tCompare;
      setCurrentTime(tMain);
    } else {
      videoRef.current.currentTime = seekTime;
      if (compareVideoRef.current) compareVideoRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  }, [currentFrameIndex, fps, isDualMode, duration]);

  useEffect(() => {
    if (videoRef.current) {
      if (isPaused) {
        videoRef.current.pause();
        compareVideoRef.current?.pause();
        setIsPlaying(false);
      }
    }
  }, [isPaused]);

  useEffect(() => {
    if (fileType !== 'video' || !videoRef.current) return;
    if (anyDrawingToolActive) {
      videoRef.current.pause();
      compareVideoRef.current?.pause();
      setIsPlaying(false);
    }
  }, [anyDrawingToolActive, fileType]);
  // Keyboard shortcuts - Space tuşu ile video durdur/başlat
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const inEditable =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.contentEditable === 'true');

      /* Çizim toolbox açıkken Esc yalnızca araçları kapatır (FileDetail); range temizlenmesin */
      if (
        e.code === 'Escape' &&
        !inEditable &&
        fileType === 'video' &&
        (rangeSliderVisible || isRangeLooping) &&
        !anyDrawingToolActive
      ) {
        e.preventDefault();
        handleCancelRangeSelection();
        return;
      }

      if (
        fileType === 'video' &&
        !inEditable &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        e.repeat === false
      ) {
        if (e.code === 'KeyM') {
          e.preventDefault();
          handleToggleMute();
          return;
        }
        if (e.code === 'KeyL') {
          e.preventDefault();
          handleToggleFullLoop();
          return;
        }
        if (e.code === 'KeyF') {
          e.preventDefault();
          handleFullScreen();
          return;
        }
        if (e.code === 'KeyK') {
          e.preventDefault();
          handlePlayPause();
          return;
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          if (!videoRef.current || !duration) return;
          e.preventDefault();
          const direction = e.key === 'ArrowLeft' ? 'back' : 'forward';
          // Kare kare ilerle: oynatmayı durdur ve tek kare git.
          if (!videoRef.current.paused) videoRef.current.pause();
          if (compareVideoRef.current && !compareVideoRef.current.paused) {
            compareVideoRef.current.pause();
          }
          const frameDur = 1 / effectiveFps;
          const before = videoRef.current.currentTime;
          const frameIndex = Math.round(before / frameDur);
          const nextIndex = frameIndex + (direction === 'back' ? -1 : 1);
          const target = Math.max(0, Math.min(duration, nextIndex * frameDur));
          seekBothVideos(target);
          const after = videoRef.current.currentTime;
          if (Math.abs(after - before) > frameDur / 2) {
            flashSeekSkipFeedback(direction);
            onSeek?.();
            compareVideo?.onSeek?.();
          }
          return;
        }
      }

      if (e.code === 'Space' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (inEditable) {
          return;
        }
        e.preventDefault();
        handlePlayPause();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlePlayPause stable
  }, [
    isPlaying,
    duration,
    fileType,
    rangeSliderVisible,
    isRangeLooping,
    anyDrawingToolActive,
    handleCancelRangeSelection,
    handleToggleMute,
    handleToggleFullLoop,
    handleFullScreen,
    seekBothVideos,
    effectiveFps,
    onSeek,
    compareVideo,
    flashSeekSkipFeedback,
  ]);

  const effectiveToolbox = isDualMode
    ? (activeVideoPanel === 1 ? controlBarToolbox : compareVideo?.controlBarToolbox)
    : controlBarToolbox;

  const rootClass =
    fileType === 'video'
      ? `${styles.videoWithChrome}${
          effectiveToolbox ? '' : ` ${styles.videoWithChromeNoToolbox}`
        }`
      : styles.videoPlayerRootImage;

  return (
    <div className={rootClass}>
      <div
        ref={playerWrapperRef}
        className={`${styles.playerWrapper}${
          fileType === 'image' ? ` ${styles.playerWrapperImage}` : ''
        }${isDualMode ? ` ${styles.playerWrapperDual}` : ''}`}
        onMouseEnter={() => setControlsVisible(true)}
        onMouseLeave={() => setControlsVisible(false)}
        onClick={isDualMode ? undefined : handleVideoClick}
      >
        {isDualMode && fileType === 'video' ? (
          <div
            className={`${styles.dualVideoDisplay}${
              dualCompareShowSinglePanel ? ` ${styles.dualVideoDisplaySinglePanel}` : ''
            }`}
          >
            <div
              className={`${styles.videoPanel}${
                dualCompareShowSinglePanel && activeVideoPanel !== 1
                  ? ` ${styles.videoPanelInactive}`
                  : ''
              }${
                dualCompareShowSinglePanel && activeVideoPanel === 1
                  ? ` ${styles.videoPanelActive}`
                  : ''
              }`}
              onClick={(e) => {
                onActiveVideoPanelChange?.(1);
                handleVideoClick(e);
              }}
            >
              {dualHeaders && (
                <div
                  className={styles.videoPanelHeader}
                  style={{ opacity: activeVideoPanel === 1 ? 1 : 0.5 }}
                  onClick={(e) => { e.stopPropagation(); onActiveVideoPanelChange?.(1); }}
                >
                  {dualHeaders.main}
                  {onComparePanelClose ? (
                    <button
                      type="button"
                      className={styles.panelHeaderCloseBtn}
                      aria-label="Kapat"
                      title="Kapat"
                      onClick={(e) => {
                        e.stopPropagation();
                        onComparePanelClose(1);
                      }}
                    >
                      <CloseOutlined />
                    </button>
                  ) : null}
                </div>
              )}
              <div className={styles.videoPanelContent}>
                <VideoElement
                  ref={videoRef}
                  src={src}
                  fileType={fileType}
                  poster={poster}
                  isDrawingEnabled={isDrawingEnabled}
                  isDrawingToolboxOpen={drawingToolActive}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={handleVideoEnded}
                  onVideoClick={(e) => {
                    onActiveVideoPanelChange?.(1);
                    handleVideoClick(e);
                  }}
                  onError={() => {}}
                />
                <div
                  className={`${styles.playPauseOverlay} ${controlsVisible ? styles.visible : ''} ${drawingToolActive ? styles.disabled : ''}`}
                >
                  {isPlaying ? (
                    <PauseCircleOutlined className={styles.playPauseIcon} />
                  ) : (
                    <PlayCircleOutlined className={styles.playPauseIcon} />
                  )}
                </div>
                <DrawingCanvas
                  stageSize={stageSize}
                  shapes={shapes}
                  tool={tool}
                  color={color}
                  selectedShapeId={selectedShapeId}
                  isDrawingEnabled={isDrawingEnabled && activeVideoPanel === 1}
                  onShapesChange={onShapesChange}
                  onTextToolClick={onTextToolClick}
                  onShapeSelection={onShapeSelection}
                  onDrawingComplete={onDrawingComplete}
                />
                {activeVideoPanel === 1 && (
                  <div className={styles.videoPanelOverlay} />
                )}
              </div>
            </div>
            <div
              className={`${styles.videoPanel}${
                dualCompareShowSinglePanel && activeVideoPanel !== 2
                  ? ` ${styles.videoPanelInactive}`
                  : ''
              }${
                dualCompareShowSinglePanel && activeVideoPanel === 2
                  ? ` ${styles.videoPanelActive}`
                  : ''
              }`}
              onClick={(e) => {
                onActiveVideoPanelChange?.(2);
                handleVideoClick(e);
              }}
            >
              {dualHeaders && (
                <div
                  className={styles.videoPanelHeader}
                  style={{ opacity: activeVideoPanel === 2 ? 1 : 0.5 }}
                  onClick={(e) => { e.stopPropagation(); onActiveVideoPanelChange?.(2); }}
                >
                  {dualHeaders.compare}
                  {onComparePanelClose ? (
                    <button
                      type="button"
                      className={styles.panelHeaderCloseBtn}
                      aria-label="Kapat"
                      title="Kapat"
                      onClick={(e) => {
                        e.stopPropagation();
                        onComparePanelClose(2);
                      }}
                    >
                      <CloseOutlined />
                    </button>
                  ) : null}
                </div>
              )}
              <div className={styles.videoPanelContent}>
                <VideoElement
                  ref={compareVideoRef}
                  src={compareVideo.src}
                  fileType={fileType}
                  poster={compareVideo.poster}
                  isDrawingEnabled={compareVideo.isDrawingEnabled}
                  isDrawingToolboxOpen={compareDrawingToolActive}
                  onTimeUpdate={() => {}}
                  onLoadedMetadata={handleCompareLoadedMetadata}
                  onEnded={handleVideoEnded}
                  onVideoClick={(e) => {
                    onActiveVideoPanelChange?.(2);
                    handleVideoClick(e);
                  }}
                  onError={() => {}}
                />
                <div
                  className={`${styles.playPauseOverlay} ${controlsVisible ? styles.visible : ''} ${compareDrawingToolActive ? styles.disabled : ''}`}
                >
                  {isPlaying ? (
                    <PauseCircleOutlined className={styles.playPauseIcon} />
                  ) : (
                    <PlayCircleOutlined className={styles.playPauseIcon} />
                  )}
                </div>
                <DrawingCanvas
                  stageSize={compareStageSize}
                  shapes={compareVideo.shapes}
                  tool={tool}
                  color={color}
                  selectedShapeId={compareVideo.selectedShapeId}
                  isDrawingEnabled={compareVideo.isDrawingEnabled && activeVideoPanel === 2}
                  onShapesChange={compareVideo.onShapesChange}
                  onTextToolClick={compareVideo.onTextToolClick}
                  onShapeSelection={compareVideo.onShapeSelection}
                  onDrawingComplete={compareVideo.onDrawingComplete}
                />
                {activeVideoPanel === 2 && (
                  <div className={styles.videoPanelOverlay} />
                )}
              </div>
            </div>
          </div>
        ) : fileType === 'image' ? (
          <div
            ref={imageViewportRef}
            className={styles.imageViewport}
            style={{
              cursor: imagePanInteractionEnabled
                ? imagePanDragging
                  ? 'grabbing'
                  : 'grab'
                : undefined,
            }}
            onPointerDown={handleImagePanPointerDown}
            onPointerMove={handleImagePanPointerMove}
            onPointerUp={handleImagePanPointerUp}
            onPointerCancel={handleImagePanPointerUp}
            onWheel={handleImageWheelZoom}
          >
            {imageTransform ? (
              <div
                className={styles.imagePanOuter}
                style={{ transform: `translate(${imagePan.x}px, ${imagePan.y}px)` }}
              >
                <div
                  className={styles.imagePanInner}
                  style={{
                    width: imageTransform.baseW,
                    height: imageTransform.baseH,
                    transform: `scale(${imageTransform.visualScale})`,
                  }}
                >
                  <VideoElement
                    ref={imgRef}
                    src={src}
                    fileType={fileType}
                    poster={poster}
                    isDrawingEnabled={isDrawingEnabled}
                    isDrawingToolboxOpen={drawingToolActive}
                    imageDisplaySize={{
                      width: imageTransform.baseW,
                      height: imageTransform.baseH,
                    }}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={handleVideoEnded}
                    onVideoClick={handleVideoClick}
                    onError={() => {}}
                  />
                  {imageStageSize.width > 0 && imageStageSize.height > 0 && (
                    <DrawingCanvas
                      stageSize={imageStageSize}
                      shapes={shapes}
                      tool={tool}
                      color={color}
                      selectedShapeId={selectedShapeId}
                      isDrawingEnabled={isDrawingEnabled}
                      onShapesChange={onShapesChange}
                      onTextToolClick={onTextToolClick}
                      onShapeSelection={onShapeSelection}
                      onDrawingComplete={onDrawingComplete}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div
                className={styles.imagePanOuter}
                style={{
                  position: 'relative',
                  alignSelf: 'stretch',
                  flex: '1 1 auto',
                  width: '100%',
                  minHeight: 0,
                  maxHeight: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <VideoElement
                  ref={imgRef}
                  src={src}
                  fileType={fileType}
                  poster={poster}
                  isDrawingEnabled={isDrawingEnabled}
                  isDrawingToolboxOpen={drawingToolActive}
                  imageDisplaySize={null}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={handleVideoEnded}
                  onVideoClick={handleVideoClick}
                  onError={() => {}}
                />
              </div>
            )}
          </div>
        ) : (
          <>
            <VideoElement
              ref={videoRef}
              src={src}
              fileType={fileType}
              poster={poster}
              isDrawingEnabled={isDrawingEnabled}
              isDrawingToolboxOpen={drawingToolActive}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleVideoEnded}
              onVideoClick={handleVideoClick}
              onError={() => {}}
            />

            <div
              className={`${styles.playPauseOverlay} ${controlsVisible ? styles.visible : ''} ${drawingToolActive ? styles.disabled : ''}`}
              onClick={drawingToolActive ? undefined : handleVideoClick}
            >
              {isPlaying ? (
                <PauseCircleOutlined className={styles.playPauseIcon} />
              ) : (
                <PlayCircleOutlined className={styles.playPauseIcon} />
              )}
            </div>

            <DrawingCanvas
              stageSize={stageSize}
              shapes={shapes}
              tool={tool}
              color={color}
              selectedShapeId={selectedShapeId}
              isDrawingEnabled={isDrawingEnabled}
              onShapesChange={onShapesChange}
              onTextToolClick={onTextToolClick}
              onShapeSelection={onShapeSelection}
              onDrawingComplete={onDrawingComplete}
            />
          </>
        )}

        {fileType === 'video' && seekSkipFeedback ? (
          <div
            key={seekSkipFeedback.id}
            className={`${styles.seekSkipOverlay} ${
              seekSkipFeedback.direction === 'back'
                ? styles.seekSkipOverlayBack
                : styles.seekSkipOverlayForward
            }`}
            role="status"
            aria-live="polite"
          >
            {seekSkipFeedback.direction === 'back' ? (
              <>
                <StepBackwardOutlined className={styles.seekSkipIcon} aria-hidden />
                <span>1 frame</span>
              </>
            ) : (
              <>
                <span>1 frame</span>
                <StepForwardOutlined className={styles.seekSkipIcon} aria-hidden />
              </>
            )}
          </div>
        ) : null}
      </div>

      {fileType === 'image' && (
        <div className={styles.imageBottomBar}>
          <div className={styles.imageBottomBarMain}>
            <div className={styles.imageBottomBarLeft}>
              <button
                type="button"
                className={`${styles.iconControlBtn} ${styles.iconControlBtnMedia}`}
                onClick={() =>
                  setImageZoom((z) =>
                    Math.max(IMAGE_ZOOM_MIN, z - IMAGE_ZOOM_STEP),
                  )
                }
                disabled={imageZoom <= IMAGE_ZOOM_MIN}
                aria-label="Uzaklaştır"
                title="Uzaklaştır"
              >
                <ZoomOutOutlined style={{ fontSize: 20 }} aria-hidden />
              </button>
              <button
                type="button"
                className={styles.imageZoomResetBtn}
                onClick={() => setImageZoom(1)}
                aria-label="Zoom %100"
                title="Zoom %100"
              >
                <span className={styles.imageZoomPercent} aria-live="polite">
                  {Math.round(imageZoom * 100)}%
                </span>
              </button>
              <button
                type="button"
                className={`${styles.iconControlBtn} ${styles.iconControlBtnMedia}`}
                onClick={() =>
                  setImageZoom((z) =>
                    Math.min(IMAGE_ZOOM_MAX, z + IMAGE_ZOOM_STEP),
                  )
                }
                disabled={imageZoom >= IMAGE_ZOOM_MAX}
                aria-label="Yakınlaştır"
                title="Yakınlaştır"
              >
                <ZoomInOutlined style={{ fontSize: 20 }} aria-hidden />
              </button>
            </div>
            <div className={styles.imageBottomBarSpacer} aria-hidden />
          </div>
          <div className={styles.imageBottomBarRight}>
            {controlBarToolbox ? (
              <div className={styles.bottomControlsToolbar}>
                <div className={styles.controlBarToolboxAnchor}>{controlBarToolbox}</div>
              </div>
            ) : null}
            <div className={styles.bottomControlsExpandSlot}>
              <button
                type="button"
                className={`${styles.iconControlBtn} ${styles.iconControlBtnMedia}`}
                onClick={handleFullScreen}
                aria-label="Tam ekran"
                title="Tam ekran"
              >
                <img
                  src="/images/expand.svg"
                  alt=""
                  className={styles.controlGlyphImg}
                  width={20}
                  height={20}
                />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kontrol overlay'ini video container'ının dışına taşı */}
      {fileType === 'video' && (
        <div className={`${styles.controlsOverlay} ${controlsVisible || !isPlaying ? styles.visible : ''}`}>
          <div
            className={styles.seekBarStack}
            onMouseEnter={() => setControlsVisible(true)}
          >
            {showRangeLoopCancel && !rangeSliderVisible ? (
              <button
                type="button"
                className={`${styles.rangeLoopCancelBtn} ${styles.rangeLoopCancelBtnOnSeek}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelRangeSelection();
                }}
                aria-label="Aralığı iptal et"
                title="Aralığı iptal et"
              >
                <CloseOutlined />
              </button>
            ) : null}
            <SeekBar
              seekbarRef={seekbarRef}
              currentTime={currentTime}
              duration={duration}
              comments={comments}
              fps={fps || 30}
              selectedCommentId={selectedCommentId}
              onSeekbarClick={handleSeekbarClick}
              onSeekbarDrag={handleSeekbarDrag}
              onCommentClick={onCommentClick}
              showResolved={showResolved}
              isRangeLooping={isRangeLooping}
              rangeLoopStart={rangeUi0}
              rangeLoopEnd={rangeUi1}
            >
              <RangeSlider
                showRangeSlider={rangeSliderVisible}
                rangeSliderValue={rangeSliderValue}
                duration={duration}
                showEscapeHint={rangeSliderVisible}
                onCancel={handleCancelRangeSelection}
                centerOverlay={
                  hasSegmentRange ? (
                    <div
                      className={`${styles.rangeLoopPlayOnBar} ${styles.rangeLoopPlayOnBarVisible}`}
                      style={
                        {
                          /* rc-slider/ant: tutamaç alanı için ray ~10px içeride; yüzdeyi o aralığa map et */
                          '--range-play-f': String(rangePlayCenterFraction),
                        } as React.CSSProperties
                      }
                    >
                      <button
                        type="button"
                        className={styles.rangeLoopPlayOnBarBtn}
                        onClick={handleRangeLoopPlayClick}
                        title={
                          isPlaying && isRangeLooping
                            ? 'Duraklat'
                            : 'Loop play selected range'
                        }
                        aria-label={
                          isPlaying && isRangeLooping
                            ? 'Duraklat'
                            : 'Loop play selected range'
                        }
                      >
                        {isPlaying && isRangeLooping ? (
                          <RangePopupPauseGlyph
                            className={styles.rangeLoopPlayOnBarBtnIcon}
                          />
                        ) : (
                          <img src="/images/play.svg" alt="" width={11} height={11} />
                        )}
                      </button>
                    </div>
                  ) : undefined
                }
                onRangeChange={(value) => {
                  const prevValue = rangeSliderValue;
                  if (value[0] !== prevValue[0]) {
                    if (videoRef.current) {
                      const t = seekBothVideos(value[0]);
                      const next: [number, number] = isDualMode ? [t, value[1]] : value;
                      setRangeSliderValue(next);
                      onRangeSelect?.(next);
                      compareVideo?.onRangeSelect?.(next);
                      onSeek?.();
                      compareVideo?.onSeek?.();
                    } else {
                      setRangeSliderValue(value);
                      onRangeSelect?.(value);
                      compareVideo?.onRangeSelect?.(value);
                    }
                  } else if (value[1] !== prevValue[1]) {
                    if (videoRef.current) {
                      const t = seekBothVideos(value[1]);
                      const next: [number, number] = isDualMode ? [value[0], t] : value;
                      setRangeSliderValue(next);
                      onRangeSelect?.(next);
                      compareVideo?.onRangeSelect?.(next);
                      onSeek?.();
                      compareVideo?.onSeek?.();
                    } else {
                      setRangeSliderValue(value);
                      onRangeSelect?.(value);
                      compareVideo?.onRangeSelect?.(value);
                    }
                  } else {
                    setRangeSliderValue(value);
                    onRangeSelect?.(value);
                    compareVideo?.onRangeSelect?.(value);
                  }
                }}
                onRangeChangeComplete={(value) => {
                  if (!videoRef.current) return;
                  const t = seekBothVideos(value[0]);
                  const next: [number, number] = isDualMode ? [t, value[1]] : value;
                  setRangeSliderValue(next);
                  onRangeSelect?.(next);
                  compareVideo?.onRangeSelect?.(next);
                  onSeek?.();
                  compareVideo?.onSeek?.();
                }}
              />
            </SeekBar>
          </div>
          <VideoControls
            isPlaying={isPlaying}
            volume={volume}
            currentTime={currentTime}
            duration={duration}
            fps={fps}
            currentFrameIndex={currentFrameIndex}
            onPlayPause={handlePlayPause}
            onVolumeChange={handleVolumeChange}
            onFullScreen={handleFullScreen}
            isLooping={isLooping && !isRangeLooping}
            onToggleLoop={handleToggleFullLoop}
            toolbarSlot={isDualMode ? effectiveToolbox : controlBarToolbox}
            compareCommentToggleSlot={compareCommentToggleSlot}
          />
        </div>
      )}
    </div>
  );
};

export default VideoPlayer; 